import React, { createContext, useContext, useState, useEffect } from 'react';
import { cartAPI } from '../services/api';
import { useAuth } from './AuthContextNew';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalItems, setTotalItems] = useState(0);

  const { user, isGuest } = useAuth();
  
  // Function to get user ID from JWT token
  const getUserIdFromToken = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      
      // Decode JWT token (base64 decode the payload part)
      const payload = token.split('.')[1];
      if (!payload) return null;
      
      const decodedPayload = JSON.parse(atob(payload));
      
      return decodedPayload.id || decodedPayload.userId || decodedPayload._id;
    } catch (error) {
      console.error('❌ Error decoding JWT token:', error);
      return null;
    }
  };
  
  // Get user ID from token if user object doesn't have it
  const effectiveUserId = user?.id || user?._id || getUserIdFromToken();

  // Load cart from backend on mount
  useEffect(() => {
    
    const loadCartWithAuth = async () => {
      try {
        // Check if user is authenticated first
        if (user && !isGuest) {
          // Add a small delay to ensure auth is fully established
          setTimeout(() => {
            refreshCart();
          }, 500);
        } else {
          // Load from localStorage for guest users
          const savedCart = localStorage.getItem('cart');
          if (savedCart) {
            try {
              const cartData = JSON.parse(savedCart);
              setItems(cartData.items || []);
              setTotalItems(cartData.totalItems || 0);
            } catch (error) {
              console.error('Error loading cart from localStorage:', error);
            }
          }
        }
      } catch (error) {
        console.error('Cart Context - Auth check failed:', error);
      }
    };
    
    // Add a longer initial delay to ensure the app is fully loaded
    setTimeout(() => {
      loadCartWithAuth();
    }, 1000);
  }, [user, isGuest]);



  const refreshCart = async (retryCount = 0) => {
    try {
      setLoading(true);
      setError(null);
      
      // Debug authentication before making the call
      const token = localStorage.getItem('token');
      
      if (!token && retryCount < 3) {
        setTimeout(() => {
          refreshCart(retryCount + 1);
        }, 1000);
        return;
      }
      
      // Check if we have a valid user ID
      if (!effectiveUserId) {
        console.error('❌ Cart Context - No valid user ID found');
        setError('User ID not found');
        return;
      }
      
      const response = await cartAPI.getCustomerCart(null);
      
      if (response.success) {
        
        // The cart data is in response.data.cart (not response.message.cart)
        const cartData = response.data?.cart;
        
        if (!cartData || !cartData.items) {
          console.error('❌ Cart Context - No cart data or items found');
          throw new Error('Invalid cart data structure');
        }
        
        // Transform backend cart data to match frontend format
        const transformedItems = cartData.items.map(item => {
          return {
            id: item.productId || item.serviceId, // This is the product/service ID from the backend
            _id: item.id, // This is the cart item ID
            cartItemId: item.id, // This is the MongoDB ObjectId of the cart item for API calls
            name: item.name,
            price: item.price,
            originalPrice: item.originalPrice,
            discount: item.discount,
            quantity: item.quantity,
            image: item.image,
            stock: item.stock,
            itemType: item.itemType || 'product',
            providerId: item.providerId || 'store',
            providerName: item.providerName || (item.providerId === 'store' ? 'NISTYLEZ Store' : item.providerId || 'Provider'),
            duration: item.duration || item.service?.duration // Add duration field for services
          };
        });
        
        setItems(transformedItems);
        setTotalItems(cartData.itemCount || 0);
      } else {
        throw new Error(response.message || 'Failed to load cart');
      }
    } catch (error) {
      console.error('Cart Context - Error loading cart:', error);
      
      // Retry if it's an authentication error and we haven't exceeded retry limit
      if (error.message?.includes('Access denied') && retryCount < 3) {
        setTimeout(() => {
          refreshCart(retryCount + 1);
        }, 2000);
        return;
      }
      
      setError('Failed to load cart');
      // Fallback to localStorage if API fails
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        try {
          const cartData = JSON.parse(savedCart);
          setItems(cartData.items || []);
          setTotalItems(cartData.totalItems || 0);
        } catch (e) {
          console.error('Error loading cart from localStorage:', e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (item) => {
    try {
      setLoading(true);
      setError(null);
      
      if (isGuest) {
        // Handle guest cart with localStorage
        const existingItem = items.find(i => i.id === item.id);
        if (existingItem) {
          const updatedItems = items.map(i => 
            i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
          );
          setItems(updatedItems);
          setTotalItems(prev => prev + 1);
          localStorage.setItem('cart', JSON.stringify({ items: updatedItems, totalItems: totalItems + 1 }));
        } else {
          const newItems = [...items, { ...item, quantity: 1 }];
          setItems(newItems);
          setTotalItems(prev => prev + 1);
          localStorage.setItem('cart', JSON.stringify({ items: newItems, totalItems: totalItems + 1 }));
        }
      } else {
        // Handle authenticated cart with API
        if (!effectiveUserId) {
          console.error('❌ Cart Context - No valid user ID for addToCart');
          setError('User ID not found');
          return;
        }
        
        
        // Determine if it's a product or service
        const isService = item.itemType === 'service';
        const productId = isService ? null : item.id;
        const serviceId = isService ? item.id : null;
        const providerId = isService ? item.providerId : 'store';
        const providerName = isService ? item.providerName : null;
        
        
        const response = await cartAPI.addToCustomerCart(null, productId, 1, serviceId, item.itemType, providerId, providerName, {});
        
        if (response.success) {
       //   console.log('✅ Cart Context - Item added successfully, reloading cart...');
          // Force reload cart from API to get updated data
          setTimeout(async () => {
            try {
        //      console.log('🔄 Cart Context - Reloading cart from API...');
              const cartResponse = await cartAPI.getCustomerCart(null);
         //     console.log('🔄 Cart Context - Cart reload response:', cartResponse);
              
              if (cartResponse.success) {
          //      console.log('🔄 Cart Context - Cart reload response:', cartResponse);
                
                // The cart data is in response.data.cart (not response.message.cart)
                const cartData = cartResponse.data?.cart;
           //     console.log('🔄 Cart Context - Cart data from reload:', cartData);
                
                if (cartData && cartData.items) {
                  const transformedItems = cartData.items.map(item => {
                    return {
                      id: item.productId || item.serviceId,
                      _id: item.id,
                      cartItemId: item.id, // This is the MongoDB ObjectId of the cart item
                      name: item.name,
                      price: item.price,
                      originalPrice: item.originalPrice,
                      discount: item.discount,
                      quantity: item.quantity,
                      image: item.image,
                      stock: item.stock,
                      itemType: item.itemType || 'product',
                      providerId: item.providerId || 'store',
                      providerName: item.providerName || (item.providerId === 'store' ? 'NISTYLEZ Store' : item.providerId || 'Provider'),
                      duration: item.duration || item.service?.duration // Add duration field for services
                    };
                  });
                  setItems(transformedItems);
                  setTotalItems(cartData.itemCount || 0);
                }
              } else {
                console.error('❌ Cart Context - Failed to reload cart:', cartResponse);
              }
            } catch (error) {
              console.error('❌ Cart Context - Error reloading cart:', error);
            }
          }, 100);
        } else {
          console.error('❌ Cart Context - Failed to add item:', response);
          throw new Error(response.message || 'Failed to add item to cart');
        }
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      setError('Failed to add item to cart: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (cartItemId) => {
    try {
      setLoading(true);
      setError(null);
      
      if (isGuest) {
        // Handle guest cart with localStorage
        const updatedItems = items.filter(item => item.cartItemId !== cartItemId);
        const removedItem = items.find(item => item.cartItemId === cartItemId);
        const newTotalItems = totalItems - (removedItem ? removedItem.quantity : 0);
        setItems(updatedItems);
        setTotalItems(newTotalItems);
        localStorage.setItem('cart', JSON.stringify({ items: updatedItems, totalItems: newTotalItems }));
      } else {
        // Handle authenticated cart with API
        const response = await cartAPI.removeFromCart(cartItemId);
        if (response.success) {
          // Update local state immediately
          const removedItem = items.find(item => item.cartItemId === cartItemId);
          const updatedItems = items.filter(item => item.cartItemId !== cartItemId);
          const newTotalItems = totalItems - (removedItem ? removedItem.quantity : 0);
          setItems(updatedItems);
          setTotalItems(newTotalItems);
        } else {
          throw new Error(response.message || 'Failed to remove item from cart');
        }
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      setError('Failed to remove item from cart: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (cartItemId, quantity) => {
    if (quantity <= 0) {
      await removeFromCart(cartItemId);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      if (isGuest) {
        // Handle guest cart with localStorage
        const updatedItems = items.map(item => 
          item.cartItemId === cartItemId ? { ...item, quantity } : item
        );
        const newTotalItems = updatedItems.reduce((total, item) => total + item.quantity, 0);
        setItems(updatedItems);
        setTotalItems(newTotalItems);
        localStorage.setItem('cart', JSON.stringify({ items: updatedItems, totalItems: newTotalItems }));
      } else {
        // Handle authenticated cart with API
        const response = await cartAPI.updateCartItemQuantity(cartItemId, quantity);
        if (response.success) {
          // Reload cart from API
          await refreshCart();
        } else {
          throw new Error(response.message || 'Failed to update cart item');
        }
      }
    } catch (error) {
      console.error('Error updating cart item:', error);
      setError('Failed to update cart item: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (isGuest) {
        // Handle guest cart with localStorage
        setItems([]);
        setTotalItems(0);
        localStorage.removeItem('cart');
      } else {
        // Handle authenticated cart with API
        const response = await cartAPI.clearCart();
        if (response.success) {
          setItems([]);
          setTotalItems(0);
        } else {
          throw new Error(response.message || 'Failed to clear cart');
        }
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      setError('Failed to clear cart: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getItemQuantity = (itemId) => {
    const item = items.find(i => i.id === itemId || i._id === itemId);
    return item ? item.quantity : 0;
  };

  const isInCart = (itemId) => {
    return items.some(item => item.id === itemId || item._id === itemId);
  };

  const getCartTotal = (providerId = null) => {
    const filteredItems = providerId 
      ? items.filter(item => item.providerId === providerId)
      : items;
    return filteredItems.reduce((total, item) => {
      const price = typeof item.price === 'string' 
        ? parseFloat(item.price.replace(/[$,]/g, '')) 
        : parseFloat(item.price) || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  const getItemsByProvider = (providerId) => {
    return items.filter(item => item.providerId === providerId);
  };

  const getItemsByType = (itemType) => {
    return items.filter(item => item.itemType === itemType);
  };

  const getProviderIds = () => {
    return [...new Set(items.map(item => item.providerId))];
  };

  const getCartSummary = () => {
    const providers = getProviderIds();
    const summary = providers.map(providerId => {
      const providerItems = getItemsByProvider(providerId);
      const providerName = providerItems.length > 0 ? providerItems[0].providerName : 'Provider';
      
      return {
        providerId,
        providerName,
        items: providerItems,
        total: getCartTotal(providerId),
        products: providerItems.filter(item => item.itemType === 'product'),
        services: providerItems.filter(item => item.itemType === 'service'),
        offers: providerItems.filter(item => item.itemType === 'offer')
      };
    });
    
    return summary;
  };

  const clearProviderCart = async (providerId) => {
    try {
  //    console.log('🧹 [CART CONTEXT] Clearing cart for provider:', providerId);
      
      // Update local state immediately
      const updatedItems = items.filter(item => item.providerId !== providerId);
      setItems(updatedItems);
      const newTotalItems = updatedItems.reduce((total, item) => total + item.quantity, 0);
      setTotalItems(newTotalItems);
      
      // Update localStorage for guest users
      if (isGuest) {
        localStorage.setItem('cart', JSON.stringify({ items: updatedItems, totalItems: newTotalItems }));
  //      console.log('✅ [CART CONTEXT] Guest cart cleared locally for provider:', providerId);
      } else {
        // For authenticated users, also clear the backend cart
        try {
          const response = await cartAPI.clearProviderCart(providerId);
          if (response.success) {
   //         console.log('✅ [CART CONTEXT] Backend cart cleared for provider:', providerId);
          } else {
            console.warn('⚠️ [CART CONTEXT] Backend cart clear failed:', response.message);
          }
        } catch (error) {
          console.error('❌ [CART CONTEXT] Error clearing backend cart:', error);
          // Don't fail the operation if backend clear fails
        }
      }
    } catch (error) {
      console.error('❌ [CART CONTEXT] Error in clearProviderCart:', error);
    }
  };

  const forceRefreshCart = async () => {
    await refreshCart(0);
  };

  // Helper functions for Home page compatibility
  const isItemInCart = (item) => {
    return items.some(cartItem => {
      const idMatch = cartItem.id === item._id || cartItem.id === item.id;
      // For services, check if both items are services and have matching provider IDs
      if (item.providerId || item.provider?._id) {
        // This is a service - check if cart item is also a service with same provider
        return idMatch && cartItem.itemType === 'service' && cartItem.providerId === (item.providerId || item.provider?._id);
      } else {
        // This is a product - check if cart item is also a product
        return idMatch && cartItem.itemType === 'product';
      }
    });
  };

  const getItemQuantityInCart = (item) => {
    const cartItem = items.find(cartItem => {
      const idMatch = cartItem.id === item._id || cartItem.id === item.id;
      // For services, check if both items are services and have matching provider IDs
      if (item.providerId || item.provider?._id) {
        // This is a service - check if cart item is also a service with same provider
        return idMatch && cartItem.itemType === 'service' && cartItem.providerId === (item.providerId || item.provider?._id);
      } else {
        // This is a product - check if cart item is also a product
        return idMatch && cartItem.itemType === 'product';
      }
    });
    return cartItem ? cartItem.quantity : 0;
  };

  const updateItemQuantity = (item, newQuantity) => {
    const cartItem = items.find(cartItem => {
      const idMatch = cartItem.id === item._id || cartItem.id === item.id;
      // For services, check if both items are services and have matching provider IDs
      if (item.providerId || item.provider?._id) {
        // This is a service - check if cart item is also a service with same provider
        return idMatch && cartItem.itemType === 'service' && cartItem.providerId === (item.providerId || item.provider?._id);
      } else {
        // This is a product - check if cart item is also a product
        return idMatch && cartItem.itemType === 'product';
      }
    });

    if (cartItem) {
      updateQuantity(cartItem.cartItemId, newQuantity);
    }
  };

  const value = {
    items,
    totalItems,
    loading,
    error,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    clearProviderCart,
    getCartTotal,
    getItemQuantity,
    isInCart,
    getItemsByProvider,
    getItemsByType,
    getProviderIds,
    getCartSummary,
    refreshCart: forceRefreshCart,
    // Home page compatibility functions
    isItemInCart,
    getItemQuantityInCart,
    updateItemQuantity,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}; 